import {Action} from '@ngrx/store';
import {Update} from '@ngrx/entity';
import { DashboardState } from './treatment.reducer';
import { CaseAssessmentData, ChargeableRefinementPackage, FollowupPackageType, InterimScanOption, TreatmentModel, UpgradeModel } from '../models/treatment.model';
import { ProductKey } from 'libs/product/src/lib/models/product.model';
import { CreateWebviewerDto, UpdateWebviewerDto, WebviewerDocument } from 'libs/web-viewer/src/lib/models/webviewer-tms-api.model';
import { ReleaseData } from 'libs/anamnese/src/lib/models/anamnese.model';

export enum TreatmentActionTypes {
  TreatmentRequested = '[Treatment Resolver] Treatment Requested',
  TreatmentSelected = '[Treatments Component] Treatment Selected',
  LoadTreatments = '[Treatment] Load Treatments',
  AddTreatment = '[Treatment] Add Treatment',
  UpsertTreatment = '[Treatment] Upsert Treatment',
  UpsertTreatmentOnDb = '[Treatment] Upsert Treatment on DB',
  AddTreatments = '[Treatment] Add Treatments',
  UpsertTreatments = '[Treatment] Upsert Treatments',
  UpdateTreatment = '[Treatment] Update Treatment',
  UpdateTreatments = '[Treatment] Update Treatments',
  DeleteTreatment = '[Treatment] Delete Treatment',
  DeleteTreatments = '[Treatment] Delete Treatments',
  ClearTreatments = '[Treatment] Clear Treatments',
  OrderNextPackage = '[Treatment] Order Next Package',
  SaveDashboardState = '[Treatment Dashboard] Save Dashboard State',
  AssignAndGoToTreatment = '[Treatment Dashboard] Assign and Go to Treatment',
  InitiateSubsequentOrder = '[Treatment] Initiate Subsequent Order',
  InitiateCaseAssessmentOrder = '[Treatment] Initiate Case Assessment Order',
  /**
   * @deprecated
   */
  SubmitComplaint = '[Treatment] Submit Complaint',
  /**
   * @deprecated
   */
  SubmitComplaintSuccess = '[Treatment] Submit Complaint Success',
  SubmitReleaseRequest = '[Treatment] Submit Release Request',
  SubmitReleaseRequestFunctionAL = '[Treatment] Submit Release Request FunctionAL',
  RequestRefinement = '[Treatment] Request Refinement',
  RequestRefinementRelease = '[Treatment] Request Refinement Release',
  ReleaseRefinementRequest = '[Treatment] Release Refinement Request',
  RejectRefinementRequest = '[Treatment] Reject Refinement Request',
  SubmitCaseAssessment = '[Treatment] Submit Case Assessment',
  RejectCaseAssessmentRequest = '[Treatment] Reject Case Assessment Request',
  CreateWebviewerModification = '[Treatment] Create Webviewer Modification',
  CreateWebviewerModificationSuccess = '[Treatment] Create Webviewer Modification Success',
  UpdateWebviewerModification = '[Treatment] Update Webviewer Modification',
  UpdateWebviewerModificationSuccess = '[Treatment] Update Webviewer Modification Success',
}

export class TreatmentRequested implements Action {
		readonly type = TreatmentActionTypes.TreatmentRequested;
		constructor(public payload: { id: string }) { }
}
export class TreatmentSelected implements Action {
	readonly type = TreatmentActionTypes.TreatmentSelected;
	constructor( public payload: string) {}
}
export class LoadTreatments implements Action {
  readonly type = TreatmentActionTypes.LoadTreatments;

  constructor(public payload: { treatments: TreatmentModel[] }) {}
}

export class AddTreatment implements Action {
  readonly type = TreatmentActionTypes.AddTreatment;

  constructor(public payload: { treatment: TreatmentModel }) {}
}

export class UpsertTreatment implements Action {
  readonly type = TreatmentActionTypes.UpsertTreatment;

  constructor(public payload: { treatment: TreatmentModel }) {}
}
export class UpsertTreatmentOnDb implements Action {
	readonly type = TreatmentActionTypes.UpsertTreatmentOnDb;

	constructor(public payload: { treatmentId: string, diff: any }) {}
}
export class AddTreatments implements Action {
  readonly type = TreatmentActionTypes.AddTreatments;

  constructor(public payload: { treatments: TreatmentModel[] }) {}
}

export class UpsertTreatments implements Action {
  readonly type = TreatmentActionTypes.UpsertTreatments;

  constructor(public payload: { treatments: TreatmentModel[] }) {}
}

export class UpdateTreatment implements Action {
  readonly type = TreatmentActionTypes.UpdateTreatment;

  constructor(public payload: { treatment: Update<TreatmentModel> }) {}
}

export class UpdateTreatments implements Action {
  readonly type = TreatmentActionTypes.UpdateTreatments;

  constructor(public payload: { treatments: Update<TreatmentModel>[] }) {}
}

export class DeleteTreatment implements Action {
  readonly type = TreatmentActionTypes.DeleteTreatment;

  constructor(public payload: { id: string }) {}
}

export class DeleteTreatments implements Action {
  readonly type = TreatmentActionTypes.DeleteTreatments;

  constructor(public payload: { ids: string[] }) {}
}

export class ClearTreatments implements Action {
  readonly type = TreatmentActionTypes.ClearTreatments;
}
export class OrderNextPackage implements Action {
	readonly type = TreatmentActionTypes.OrderNextPackage;
	constructor(public payload: {treatmentId: string, packageType: FollowupPackageType, interimScanOption: InterimScanOption}) {}
}

export class SaveDashboardState implements Action {
	readonly type = TreatmentActionTypes.SaveDashboardState;
	constructor( public payload: {tab: number, state: DashboardState}) {}
}

export class AssignAndGoToTreatment implements Action {
	readonly type = TreatmentActionTypes.AssignAndGoToTreatment;
	constructor(public payload: {
		treatment: TreatmentModel,
		username: string,
	}) { }
}

export class InitiateSubsequentOrder implements Action {
	readonly type = TreatmentActionTypes.InitiateSubsequentOrder;
	constructor(public payload: {
		productKey: ProductKey,
		patientId: string,
		treatmentId: string,
		quota: {ujAvailable: boolean, ljAvailable: boolean}
	}) { }
}

export class InitiateCaseAssessmentOrder implements Action {
	readonly type = TreatmentActionTypes.InitiateCaseAssessmentOrder;
	constructor(public payload: {
		caseAssessmentTreatment: TreatmentModel
	}) { }
}

export class SubmitReleaseRequest implements Action {
	readonly type = TreatmentActionTypes.SubmitReleaseRequest;
	constructor(
		public payload: {
			treatmentId: string;
			releaseRequest: {
				releaseData: ReleaseData;
				totalPlannedSteps: number;
				upgrade?: UpgradeModel;
				language?: "de" | "en";
			};
		}
	) {}
}

/**
 * @deprecated
 */
export class SubmitComplaint implements Action {
	readonly type = TreatmentActionTypes.SubmitComplaint;
	constructor(public payload: { treatment: TreatmentModel }) { }
}

/**
 * @deprecated
 */
export class SubmitComplaintSuccess implements Action {
	readonly type = TreatmentActionTypes.SubmitComplaintSuccess;
	constructor(public payload: { treatment: TreatmentModel }) { }
}

export class SubmitReleaseRequestFunctionAL implements Action {
	readonly type = TreatmentActionTypes.SubmitReleaseRequestFunctionAL;
	constructor(public payload: { treatmentId: string, language: "de" | "en" }) { }
}

export class RequestRefinement implements Action {
	readonly type = TreatmentActionTypes.RequestRefinement;
	constructor(public payload: {treatmentId: string, comment?: string}) {}
}

export class RequestRefinementRelease implements Action {
	readonly type = TreatmentActionTypes.RequestRefinementRelease;
	constructor(public payload: {treatmentId: string, plannedAligners: ChargeableRefinementPackage | number }) {}
}
export class ReleaseRefinementRequest implements Action {
	readonly type = TreatmentActionTypes.ReleaseRefinementRequest;
	constructor(public payload: {treatmentId: string}) {}
}

export class RejectRefinementRequest implements Action {
	readonly type = TreatmentActionTypes.RejectRefinementRequest;
	constructor(public payload: {treatmentId: string}) {}
}
export class SubmitCaseAssessment implements Action {
	readonly type = TreatmentActionTypes.SubmitCaseAssessment;
	constructor(public payload: {treatmentId: string, caseAssessmentData: CaseAssessmentData}) {}
}

export class RejectCaseAssessmentRequest implements Action {
	readonly type = TreatmentActionTypes.RejectCaseAssessmentRequest;
	constructor(public payload: {treatmentId: string}) {}
}

export class CreateWebviewerModification implements Action {
	readonly type = TreatmentActionTypes.CreateWebviewerModification;
	constructor(
		public payload: {
			treatmentId: string;
			modification: CreateWebviewerDto;
			webviewerUrl: string;
			language?: "de" | "en";
		}
	) {}
}

export class CreateWebviewerModificationSuccess implements Action {
	readonly type = TreatmentActionTypes.CreateWebviewerModificationSuccess;
	constructor(public payload: { modification: WebviewerDocument }) {}
}

export class UpdateWebviewerModification implements Action {
	readonly type = TreatmentActionTypes.UpdateWebviewerModification;
	constructor(
		public payload: {
			treatmentId: string;
			modificationId: string;
			modification: UpdateWebviewerDto;
			language?: "de" | "en";
		}
	) {}
}

export class UpdateWebviewerModificationSuccess implements Action {
	readonly type = TreatmentActionTypes.UpdateWebviewerModificationSuccess;
	constructor(public payload: { modification: WebviewerDocument }) {}
}

export type TreatmentActions =
 | TreatmentRequested
 | TreatmentSelected
 | LoadTreatments
 | AddTreatment
 | UpsertTreatment
 | UpsertTreatmentOnDb
 | AddTreatments
 | UpsertTreatments
 | UpdateTreatment
 | UpdateTreatments
 | DeleteTreatment
 | DeleteTreatments
 | ClearTreatments
 | OrderNextPackage
 | SaveDashboardState
 | SubmitComplaint
 | SubmitComplaintSuccess
 | AssignAndGoToTreatment
 | InitiateSubsequentOrder
 | InitiateCaseAssessmentOrder
 | SubmitReleaseRequest
 | SubmitReleaseRequestFunctionAL
 | RequestRefinement
 | RequestRefinementRelease
 | ReleaseRefinementRequest
 | RejectRefinementRequest
 | SubmitCaseAssessment
 | RejectCaseAssessmentRequest
 | CreateWebviewerModification
 | CreateWebviewerModificationSuccess
 | UpdateWebviewerModification
 | UpdateWebviewerModificationSuccess;
